// One Asset library record (HD-10), shown two ways with the same body and the same calls:
//   AssetPanel   the Console's Expanded-row panel (DESIGN rev 2.1 §2.5) on /assets for staff
//   AssetDialog  a ui/Modal, where there is no Grid to sit beside: the Portal page (§3.3 has no panel) and the Clio chat
// The calls are the pre-HD-10 AssetCardModal's, with its role rules:
//   GET    /api/assets/:id                          on open
//   PATCH  /api/assets/:id { notes }                on leaving the notes field (upload roles)
//   PATCH  /api/assets/:id { aiTags }               Save tags (Admin / Power User)
//   PATCH  /api/assets/:id/move { folderId }        the folder field (upload roles, staff only)
//   POST   /api/assets/:id/reanalyze, then GET /api/assets/:id every 2 s while pending   Retry analysis (Admin, failed)
//   POST   /api/assets/:id/reanalyze-guided { hint }                                     Re-examine with Clio (Admin)
//   GET    /api/assets/:id/download → the browser downloads the presigned URL             Download
//   PATCH  /api/assets/:id/archive                  Archive, after a confirm (upload roles)
//   DELETE /api/assets/:id                          Delete, after a two-step confirm (Admin, Editor; the API checks)
import { Fragment, useEffect, useRef, useState } from 'react';
import { Archive, ChevronDown, Download, Pencil, Plus, RotateCw, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import { PROP_TYPES, TAG_KEYS, fmtBytes, fmtDate, propLabel, userCanUpload, userIsAdmin } from '../lib/assetLibrary';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import ErrorState from '../ui/ErrorState';
import ExpandedRowPanel from '../ui/ExpandedRowPanel';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import Tag from '../ui/Tag';
import Textarea from '../ui/Textarea';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { clean } from '../shell/copy';

// The image analysis where it is not done: a Badge (the Grid's Type column, the Portal rows).
export const analysisBadge = a => (a.analysisStatus === 'pending' ? <Badge tone="info">Analyzing</Badge>
  : a.analysisStatus === 'failed' ? <Badge tone="danger">Analysis failed</Badge> : null);

const errText = (err, fallback) => err?.response?.data?.error || fallback;

function useAssetRecord({ assetId, user, onArchived, onDeleted, onFolderChanged, onClose }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [asset, setAsset] = useState(null);
  const [state, setState] = useState('loading');
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [busy, setBusy] = useState(null);           // 'download' | 'archive' | 'delete' | 'retry' | 'tags' | 'move' | 'reexamine'
  const [editingTags, setEditingTags] = useState(false);
  const [tagDraft, setTagDraft] = useState({});
  const [hint, setHint] = useState('');
  const retryTimer = useRef(null);
  const savedTimer = useRef(null);

  const canUp = userCanUpload(user);
  const isAdm = userIsAdmin(user);
  const isEditor = !!user?.roles?.includes('Editor');
  const perms = {
    canUp, isAdm,
    canMoveFolder: canUp && !user?.isExternal,
    canDelete: isAdm || isEditor,   // the API enforces the editor-vs-editor and internal / external wall
  };

  useEffect(() => {
    let live = true;
    setState('loading');
    api.get(`/api/assets/${assetId}`)
      .then(r => { if (!live) return; setAsset(r.data); setNotes(r.data.notes || ''); setTagDraft(r.data.aiTags || {}); setState('data'); })
      .catch(() => { if (live) setState('error'); });
    return () => { live = false; if (retryTimer.current) clearInterval(retryTimer.current); clearTimeout(savedTimer.current); };
  }, [assetId]);

  const name = clean(asset?.fileName) || 'this asset';

  const actions = {
    async saveNotes() {
      try {
        const r = await api.patch(`/api/assets/${assetId}`, { notes });
        setAsset(a => ({ ...a, notes: r.data.notes }));
        setNotesSaved(true);
        clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setNotesSaved(false), 2500);
      } catch (err) { toast(errText(err, 'The notes were not saved. Try again.'), { tone: 'danger' }); }
    },
    async download() {
      setBusy('download');
      try {
        const r = await api.get(`/api/assets/${assetId}/download`);
        const a = document.createElement('a');
        a.href = r.data.url; a.download = asset?.fileName || 'asset'; a.click();
      } catch (err) { toast(errText(err, 'The download did not start. Try again.'), { tone: 'danger' }); }
      finally { setBusy(null); }
    },
    async archive() {
      const ok = await confirm({ title: `Archive ${name}?`, body: 'It leaves the library and no longer shows in search.', confirmLabel: 'Archive' });
      if (!ok) return;
      setBusy('archive');
      try {
        await api.patch(`/api/assets/${assetId}/archive`);
        toast(`${name} archived.`);
        onArchived?.(); onClose?.();
      } catch (err) { toast(errText(err, 'The asset was not archived. Try again.'), { tone: 'danger' }); }
      finally { setBusy(null); }
    },
    async remove() {
      const ok = await confirm({
        title: `Delete ${name} permanently?`,
        body: 'The image and its thumbnail are deleted from cloud storage. The Asset library has no recycle bin, so this cannot be undone.',
        confirmLabel: 'Delete permanently', destructive: true,
      });
      if (!ok) return;
      setBusy('delete');
      try {
        await api.delete(`/api/assets/${assetId}`);
        toast(`${name} deleted.`);
        onDeleted?.(); onClose?.();
      } catch (err) { toast(errText(err, 'The asset was not deleted.'), { tone: 'danger' }); }
      finally { setBusy(null); }
    },
    async retry() {
      setBusy('retry');
      try {
        await api.post(`/api/assets/${assetId}/reanalyze`);
        setAsset(a => ({ ...a, analysisStatus: 'pending' }));
        retryTimer.current = setInterval(async () => {
          try {
            const r = await api.get(`/api/assets/${assetId}`);
            if (r.data.analysisStatus !== 'pending') {
              clearInterval(retryTimer.current);
              setAsset(r.data); setTagDraft(r.data.aiTags || {}); setBusy(null);
            }
          } catch { clearInterval(retryTimer.current); setBusy(null); }
        }, 2000);
      } catch (err) { setBusy(null); toast(errText(err, 'The analysis did not restart. Try again.'), { tone: 'danger' }); }
    },
    async reexamine() {
      if (!hint.trim() || busy === 'reexamine') return;
      setBusy('reexamine');
      try {
        const r = await api.post(`/api/assets/${assetId}/reanalyze-guided`, { hint: hint.trim() });
        setAsset(prev => ({ ...prev, ...r.data }));
        setTagDraft(r.data.aiTags || {});
        setHint('');
        toast('Clio re-examined the image. The description and tags are updated.');
      } catch (err) { toast(errText(err, 'The re-examination failed. Try again.'), { tone: 'danger' }); }
      finally { setBusy(null); }
    },
    async saveTags() {
      setBusy('tags');
      try {
        const r = await api.patch(`/api/assets/${assetId}`, { aiTags: tagDraft });
        setAsset(a => ({ ...a, aiTags: r.data.aiTags }));
        setEditingTags(false);
      } catch (err) { toast(errText(err, 'The tags were not saved. Try again.'), { tone: 'danger' }); }
      finally { setBusy(null); }
    },
    async move(folderId) {
      setBusy('move');
      try {
        await api.patch(`/api/assets/${assetId}/move`, { folderId: folderId || null });
        setAsset(a => ({ ...a, folderId: folderId || null }));
        onFolderChanged?.();
      } catch (err) { toast(errText(err, 'The asset was not moved. Try again.'), { tone: 'danger' }); }
      finally { setBusy(null); }
    },
  };

  return {
    asset, state, name, perms, busy, actions,
    notes, setNotes: v => { setNotes(v); setNotesSaved(false); }, notesSaved,
    editingTags, setEditingTags, tagDraft, setTagDraft, hint, setHint,
  };
}

// ── The body (image, description, tags, details, notes, admin tools) ────────────────────────────────────────────────

function TagEditor({ draft, onChange, onSave, onCancel, saving, portal }) {
  const setAt = (key, i, v) => { const arr = [...(draft[key] || [])]; arr[i] = v; onChange({ ...draft, [key]: arr }); };
  const add = key => onChange({ ...draft, [key]: [...(draft[key] || []), ''] });
  const drop = (key, i) => onChange({ ...draft, [key]: (draft[key] || []).filter((_, j) => j !== i) });
  return (
    <section className="ui-section" aria-label="Edit tags">
      <h3 className="ui-heading">Edit tags</h3>
      <Select label="Prop type" value={draft.prop_type || ''} onChange={e => onChange({ ...draft, prop_type: e.target.value })}
        options={[{ value: '', label: 'None' }, ...PROP_TYPES.map(t => ({ value: t, label: propLabel(t) }))]} />
      {TAG_KEYS.map(({ key, label, one }) => (
        <fieldset key={key} className="ui-fieldset ui-stack ui-stack--tight">
          <legend className="ui-field__label">{label}</legend>
          {(draft[key] || []).map((val, i) => (
            <span key={i} className="ui-cluster ui-cluster--nowrap">
              <Input className="ui-grow" aria-label={`${label} ${i + 1}`} value={val} onChange={e => setAt(key, i, e.target.value)} />
              <Button variant="quiet" portal={portal} iconOnly icon={X} label={`Remove ${val || `${one} ${i + 1}`}`} onClick={() => drop(key, i)} />
            </span>
          ))}
          <span><Button variant="quiet" portal={portal} icon={Plus} onClick={() => add(key)}>Add {one}</Button></span>
        </fieldset>
      ))}
      <div className="ui-cluster">
        <Button variant="primary" portal={portal} loading={saving} onClick={onSave}>Save tags</Button>
        <Button variant="quiet" portal={portal} onClick={onCancel}>Cancel</Button>
      </div>
    </section>
  );
}

function AssetBody({ rec, folders, portal }) {
  const { asset, perms, busy, actions } = rec;
  const [reexamineOpen, setReexamineOpen] = useState(false);
  const tags = asset.aiTags || {};
  const tagGroups = TAG_KEYS.filter(m => tags[m.key]?.length);
  const hasTags = tagGroups.length > 0 || !!tags.prop_type;
  const folderName = clean(asset.folderName) || clean(folders?.find(f => f.id === asset.folderId)?.name) || null;
  return (
    <>
      <div className="ui-monitor__frame ui-monitor__frame--16x9 ui-noshrink">
        {asset.cloudfrontUrl
          ? <img className="ui-contain" src={asset.cloudfrontUrl} alt={clean(asset.aiDescription) || clean(asset.fileName)} loading="lazy" />
          : <span className="ui-frame-note ui-meta">No image</span>}
      </div>

      {asset.analysisStatus === 'pending' && <Badge tone="info">Clio is analyzing this image</Badge>}
      {asset.analysisStatus === 'failed' && (
        <div className="ui-cluster">
          <Badge tone="danger">Analysis failed</Badge>
          {perms.isAdm && <Button variant="secondary" portal={portal} icon={RotateCw} loading={busy === 'retry'} onClick={actions.retry}>Retry analysis</Button>}
        </div>
      )}

      {asset.aiDescription && <p className="ui-p ui-text-2">{clean(asset.aiDescription)}</p>}

      {hasTags && !rec.editingTags && (
        <section className="ui-section" aria-label="Tags">
          <div className="ui-section__head ui-section__head--center">
            <h3 className="ui-heading">Tags</h3>
            {perms.isAdm && <span className="ui-section__actions"><Button variant="quiet" portal={portal} icon={Pencil} onClick={() => rec.setEditingTags(true)}>Edit tags</Button></span>}
          </div>
          <dl className="ui-kv">
            {tags.prop_type && <><dt>Prop type</dt><dd>{propLabel(clean(tags.prop_type))}</dd></>}
            {tagGroups.map(({ key, label }) => (
              <Fragment key={key}>
                <dt>{label}</dt>
                <dd><span className="ui-cluster">{tags[key].map((t, i) => <Tag key={i}>{clean(t)}</Tag>)}</span></dd>
              </Fragment>
            ))}
          </dl>
        </section>
      )}
      {!hasTags && perms.isAdm && !rec.editingTags && asset.analysisStatus === 'complete' && (
        <span><Button variant="quiet" portal={portal} icon={Pencil} onClick={() => rec.setEditingTags(true)}>Add tags</Button></span>
      )}
      {rec.editingTags && perms.isAdm && (
        <TagEditor draft={rec.tagDraft} onChange={rec.setTagDraft} onSave={actions.saveTags} portal={portal}
          onCancel={() => { rec.setEditingTags(false); rec.setTagDraft(asset.aiTags || {}); }} saving={busy === 'tags'} />
      )}

      <section className="ui-section" aria-label="Details">
        <h3 className="ui-heading">Details</h3>
        <dl className="ui-kv">
          <dt>Client</dt><dd>{clean(asset.clientName) || 'No client (shared)'}</dd>
          <dt>Folder</dt>
          <dd>
            {perms.canMoveFolder ? (
              <Select aria-label="Folder" value={asset.folderId || ''} disabled={busy === 'move'} onChange={e => actions.move(e.target.value || null)}
                options={[{ value: '', label: 'No folder' }, ...(folders || []).map(f => ({ value: f.id, label: clean(f.name) }))]} />
            ) : (folderName || 'No folder')}
          </dd>
          <dt>Uploaded by</dt><dd>{clean(asset.uploaderName) || 'Unknown'}</dd>
          <dt>Added</dt><dd>{fmtDate(asset.createdAt) || 'Unknown'}</dd>
          <dt>Size</dt><dd><span className="ui-tc">{fmtBytes(asset.fileSizeBytes) || 'Unknown'}</span></dd>
        </dl>
      </section>

      {asset.embeddingDoc && (
        <section className="ui-section" aria-label="Search context">
          <h3 className="ui-heading">Search context</h3>
          <p className="ui-p ui-dense ui-text-2">{clean(asset.embeddingDoc)}</p>
        </section>
      )}

      {perms.canUp ? (
        <Textarea label="Notes" rows={3} value={rec.notes} placeholder="Add a note about this asset" data-testid="assets-notes"
          hint={rec.notesSaved ? 'Saved' : undefined}
          onChange={e => rec.setNotes(e.target.value)} onBlur={actions.saveNotes} />
      ) : (
        <section className="ui-section" aria-label="Notes">
          <h3 className="ui-heading">Notes</h3>
          <p className="ui-p ui-dense ui-prewrap">{asset.notes ? clean(asset.notes) : 'No notes'}</p>
        </section>
      )}

      {perms.isAdm && (
        <section className="ui-section" aria-label="Re-examine with Clio">
          <button type="button" className="ui-disclosure ui-heading" aria-expanded={reexamineOpen} onClick={() => setReexamineOpen(o => !o)}>
            <Icon icon={ChevronDown} />Re-examine with Clio
          </button>
          {reexamineOpen && (
            <div className="ui-stack">
              <Textarea label="What Clio got wrong" rows={3} value={rec.hint} disabled={busy === 'reexamine'} placeholder="This is a pirate hat, not a wizard hat."
                hint="Name the mistake, for example the brand is Snyder's, not generic." onChange={e => rec.setHint(e.target.value)} />
              <span><Button variant="secondary" portal={portal} loading={busy === 'reexamine'} disabled={!rec.hint.trim()} onClick={actions.reexamine}>Re-examine</Button></span>
            </div>
          )}
        </section>
      )}
    </>
  );
}

function AssetFooter({ rec, portal }) {
  const { perms, busy, actions } = rec;
  return (
    <>
      <Button variant="primary" portal={portal} icon={Download} loading={busy === 'download'} data-testid="assets-download" onClick={actions.download}>Download</Button>
      {perms.canUp && <Button variant="quiet" portal={portal} icon={Archive} loading={busy === 'archive'} onClick={actions.archive}>Archive</Button>}
      {perms.canDelete && <Button variant="danger" portal={portal} icon={Trash2} loading={busy === 'delete'} onClick={actions.remove}>Delete</Button>}
    </>
  );
}

function Loading() {
  return (
    <div className="ui-stack" aria-busy="true">
      <Skeleton variant="block" height="180px" />
      <Skeleton width="90%" /><Skeleton width="70%" /><Skeleton width="50%" />
    </div>
  );
}
const LoadError = () => <ErrorState title="This asset did not load" message="It may have been archived or deleted. Close this and refresh the list." />;

export function AssetPanel({ assetId, user, folders, onClose, onArchived, onDeleted, onFolderChanged }) {
  const rec = useAssetRecord({ assetId, user, onArchived, onDeleted, onFolderChanged, onClose });
  return (
    <ExpandedRowPanel open label="Asset details" onClose={onClose}
      title={rec.state === 'data' ? rec.name : rec.state === 'loading' ? 'Loading the asset' : 'Asset not found'}
      footer={rec.state === 'data' ? <AssetFooter rec={rec} /> : undefined}>
      {rec.state === 'loading' ? <Loading /> : rec.state === 'error' ? <LoadError /> : <AssetBody rec={rec} folders={folders} />}
    </ExpandedRowPanel>
  );
}

export function AssetDialog({ assetId, user, folders, onClose, onArchived, onDeleted, onFolderChanged, portal = false }) {
  const rec = useAssetRecord({ assetId, user, onArchived, onDeleted, onFolderChanged, onClose });
  return (
    <Modal onClose={onClose} size="medium" testId="assets-dialog"
      title={rec.state === 'data' ? rec.name : rec.state === 'loading' ? 'Loading the asset' : 'Asset not found'}
      meta={rec.state === 'data' ? 'Asset library' : undefined}
      footer={rec.state === 'data' ? <><AssetFooter rec={rec} portal={portal} /><span className="ui-spacer" /><Button variant="quiet" portal={portal} onClick={onClose}>Close</Button></> : undefined}>
      <div className="ui-stack ui-stack--loose">
        {rec.state === 'loading' ? <Loading /> : rec.state === 'error' ? <LoadError /> : <AssetBody rec={rec} folders={folders} portal={portal} />}
      </div>
    </Modal>
  );
}
